import React, { useState } from 'react';
import { Modal, Button, Upload, message, Table, Tag, Steps, Card, Divider, Checkbox, Space, Alert, Progress } from 'antd';
import { InboxOutlined, EyeOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { getTourProductListForCopy, getOrderStatusList } from '../services/apiService';

const { Dragger } = Upload;

const MaritReservationImportModal = ({ open, onClose, onApply, onApplyToRegistered }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [editingData, setEditingData] = useState([]);
  const [registrationType, setRegistrationType] = useState('unregistered'); // 'unregistered' or 'registered'
  const [matchedProducts, setMatchedProducts] = useState([]); // 매칭된 상품 정보
  const [tourProducts, setTourProducts] = useState([]); // 전체 투어상품 리스트
  const [existingChecks, setExistingChecks] = useState([]);
  const [progressInfo, setProgressInfo] = useState({ active: false, total: 0, completed: 0, errors: 0, logs: [], completedByGroup: {} });

  const handleFileUpload = async (file) => {
    setLoading(true);
    setFileName(file.name);
    
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
// 예약일 기준으로 그룹핑
      const groupedData = groupByReservationDate(jsonData);
      setData(groupedData);
      setEditingData(JSON.parse(JSON.stringify(groupedData))); // 깊은 복사
      
      // 등록된 상품 모드인 경우 상품 매칭
      if (registrationType === 'registered') {
        await matchProductsWithExcel(groupedData);
      }
      await detectExistingOrders(groupedData);
      
      // 모든 주문을 기본 선택
      const allOrderIds = new Set(groupedData.map((_, index) => index));
      setSelectedOrders(allOrderIds);
      
      message.success(`${jsonData.length}개 행을 ${groupedData.length}개 예약 그룹으로 변환했습니다.`);
      setCurrentStep(1); // 상세 검토 단계로 이동
    } catch (error) {
      console.error('File processing error:', error);
      message.error('파일 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const normalizeName = (nm) => String(nm || '').replace(/\s*-\s*\d{4}-\d{2}-\d{2}$/,'').trim();

  const detectExistingOrders = async (groups) => {
    try {
      const byDate = new Map();
      groups.forEach((g, idx) => {
        const date = formatDateForAPI(g.eventStartDate);
        if (!date) return;
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date).push({ g, idx });
      });
      const checks = Array(groups.length).fill(null);
      for (const [date, arr] of byDate.entries()) {
        let list = [];
        try {
          const data = await getOrderStatusList({ startDate: date, endDate: date });
          list = Array.isArray(data) ? data : [];
        } catch (_) {}
        const codeKeys = ['상품코드','판매상품코드','ProductCode','productCode'];
        const nameKeys = ['판매상품명','상품명','ProductName','productName'];
        for (const { g, idx } of arr) {
          const codeMatch = String(g.productName || '').match(/\((\d+)\)/);
          const excelCode = codeMatch ? codeMatch[1] : null;
          let found = null;
          if (excelCode) {
            found = list.find((row) => codeKeys.some((k) => String(row?.[k] ?? '') === String(excelCode)));
          }
          if (!found) {
            const excelName = normalizeName(g.productName);
            found = list.find((row) => {
              const rowName = nameKeys.map((k) => normalizeName(row?.[k])).find((v) => !!v);
              return rowName && rowName === excelName;
            });
          }
          if (found) {
            const orderNo = found.주문번호 ?? found.orderNo ?? found.OrderNo ?? null;
            checks[idx] = { exists: true, orderNo };
          } else {
            checks[idx] = { exists: false };
          }
        }
      }
      setExistingChecks(checks);
    } catch (_) {
      setExistingChecks([]);
    }
  };

  // 엑셀 데이터와 등록된 상품 매칭
  const matchProductsWithExcel = async (groupedData) => {
    try {
      // 투어상품 리스트 조회
      const productsData = await getTourProductListForCopy();
      const products = Array.isArray(productsData) ? productsData : [];
      setTourProducts(products);
// 각 예약 그룹에 대해 상품 매칭
      const matched = groupedData.map((group) => {
        // 상품번호 추출
        const productCodeMatch = group.productName?.match(/\((\d+)\)/);
        const excelProductCode = productCodeMatch ? productCodeMatch[1] : null;
        
        if (!excelProductCode) {
          return {
            excelProductCode: null,
            matched: false,
            error: '상품번호를 찾을 수 없습니다.'
          };
        }
        
        // 상품코드로 매칭
        const matchedProduct = products.find(p => String(p.상품코드) === String(excelProductCode));
        
        if (matchedProduct) {
          return {
            excelProductCode,
            matched: true,
            product: {
              상품번호: matchedProduct.상품번호,
              상품명: matchedProduct.상품명,
              상품코드: matchedProduct.상품코드,
              도시: matchedProduct.도시,
              상품종류: matchedProduct.상품종류,
              판매여부: matchedProduct.판매여부
            }
          };
        } else {
          return {
            excelProductCode,
            matched: false,
            error: `상품코드 ${excelProductCode}에 해당하는 등록된 상품을 찾을 수 없습니다.`
          };
        }
      });
      
      setMatchedProducts(matched);
} catch (error) {
      console.error('상품 매칭 오류:', error);
      message.error('상품 매칭 중 오류가 발생했습니다.');
    }
  };

  const groupByReservationDate = (rows) => {
    const groups = {};
    
    rows.forEach((row, index) => {
      const reservationDate = row['예약일'];
      if (!reservationDate) return;
      
      if (!groups[reservationDate]) {
        groups[reservationDate] = [];
      }
      groups[reservationDate].push({ ...row, originalIndex: index });
    });

    return Object.entries(groups).map(([date, groupRows]) => {
      const firstRow = groupRows[0];
      
      // 디버깅: 여행시작일 값 확인
return {
        reservationDate: date,
        productName: `${firstRow['상품 이름']} (${firstRow['상품번호']}) - ${date}`,
        eventStartDate: firstRow['여행시작일'], // 3. 여행시작일을 행사시작일로 매핑
        travelerCount: groupRows.length,
        totalAmount: groupRows.reduce((sum, row) => {
          const amount = parseFloat(row['금액']?.toString().replace(/,/g, '') || '0');
          return sum + amount;
        }, 0),
        travelers: groupRows.map((row, idx) => ({
          name: row['여행자'] || `여행자${idx + 1}`,
          phone: row['여행자 연락처'] || '',
          email: row['이메일'] || '', // 4. 이메일 매핑 (이미 있었음)
          amount: parseFloat(row['금액']?.toString().replace(/,/g, '') || '0'),
          discount: -Math.abs(parseFloat(row['쿠폰할인']?.toString().replace(/,/g, '') || '0')),
          // 1, 2. 예약번호와 옵션정보를 비고사항으로 조합
          memo: [row['예약번호'], row['옵션정보']].filter(Boolean).join(' / ')
        }))
      };
    });
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const selectedData = editingData.filter((_, index) => selectedOrders.has(index));
      if (selectedData.length === 0) {
        message.error('등록할 주문을 선택해주세요.');
        return;
      }
      setCurrentStep(2); // 최종 확인 단계로
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleApply = async () => {
    const selectedData = editingData.filter((_, index) => selectedOrders.has(index));
    
    if (selectedData.length === 0) {
      message.error('등록할 데이터가 없습니다.');
      return;
    }

    setLoading(true);
    // 초기 프로그레스 상태 설정
    setProgressInfo({ active: true, total: selectedData.length, completed: 0, errors: 0, logs: [], completedByGroup: {} });

    const onProgress = (evt = {}) => {
      // evt: { groupIndex, step, status: 'success'|'error', message, orderNo, saleNo }
      setProgressInfo((prev) => {
        const logs = prev.logs.concat({ time: new Date().toISOString(), ...evt });
        const completedByGroup = { ...(prev.completedByGroup || {}) };
        let completed = prev.completed;
        let errors = prev.errors;
        if (evt.status === 'success' && evt.step === 'step3' && !completedByGroup[evt.groupIndex]) {
          completedByGroup[evt.groupIndex] = true;
          completed += 1;
        }
        if (evt.status === 'error' && !completedByGroup[evt.groupIndex]) {
          completedByGroup[evt.groupIndex] = true;
          completed += 1;
          errors += 1;
        }
        return { ...prev, logs, completed, errors, completedByGroup };
      });
    };
    try {
      if (registrationType === 'registered') {
        // 등록된 상품에 예약 추가
        if (onApplyToRegistered) {
          await onApplyToRegistered(selectedData, { onProgress });
        } else {
          message.error('등록된 상품 예약 기능이 활성화되지 않았습니다.');
          return;
        }
      } else {
        // 미등록 상품 예약 생성
        await onApply(selectedData, { onProgress });
      }
      message.success(`${selectedData.length}개 예약을 성공적으로 등록했습니다.`);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Apply error:', error);
      message.error('예약 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setProgressInfo((p) => ({ ...p, active: false }));
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setData([]);
    setEditingData([]);
    setFileName('');
    setSelectedOrders(new Set());
    setRegistrationType('unregistered');
    setMatchedProducts([]);
    setTourProducts([]);
    setExistingChecks([]);
  };

  const toggleOrderSelection = (index) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedOrders(newSelected);
  };

  const selectAllOrders = () => {
    const allIds = new Set(editingData.map((_, index) => index));
    setSelectedOrders(allIds);
  };

  const deselectAllOrders = () => {
    setSelectedOrders(new Set());
  };

  const columns = [
    {
      title: '예약일',
      dataIndex: 'reservationDate',
      key: 'reservationDate',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString('ko-KR')
    },
    {
      title: '상품명',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true
    },
    {
      title: '여행객 수',
      dataIndex: 'travelerCount',
      key: 'travelerCount',
      width: 100,
      render: (count) => <Tag color="blue">{count}명</Tag>
    },
    {
      title: '총 금액',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount) => `${amount.toLocaleString()}원`
    }
  ];

  const getModalTitle = () => {
    switch (currentStep) {
      case 0: return '마리트 예약 엑셀 가져오기 - 파일 업로드';
      case 1: return '마리트 예약 엑셀 가져오기 - 상세 검토';
      case 2: return '마리트 예약 엑셀 가져오기 - 최종 확인';
      default: return '마리트 예약 엑셀 가져오기';
    }
  };

  const getFooterButtons = () => {
    switch (currentStep) {
      case 0:
        return [
          <Button key="cancel" onClick={onClose}>취소</Button>
        ];
      case 1:
        const selectedCount = selectedOrders.size;
        return [
          <Button key="prev" onClick={handlePrev}>이전</Button>,
          <Button key="cancel" onClick={onClose}>취소</Button>,
          <Button 
            key="next" 
            type="primary" 
            onClick={handleNext}
            disabled={selectedCount === 0}
          >
            {selectedCount}개 주문 확인
          </Button>
        ];
      case 2:
        const finalCount = selectedOrders.size;
        return [
          <Button key="prev" onClick={handlePrev}>이전</Button>,
          <Button key="cancel" onClick={onClose}>취소</Button>,
          <Button 
            key="apply" 
            type="primary" 
            onClick={handleApply}
            loading={loading}
          >
            {finalCount}개 주문 생성하기
          </Button>
        ];
      default:
        return [<Button key="cancel" onClick={onClose}>취소</Button>];
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={open}
      onCancel={() => {
        handleReset();
        onClose();
      }}
      width={currentStep === 1 ? 1200 : 800}
      footer={getFooterButtons()}
    >
      <Steps current={currentStep} className="mb-6">
        <Steps.Step title="파일 업로드" icon={<InboxOutlined />} />
        <Steps.Step title="상세 검토" icon={<EyeOutlined />} />
        <Steps.Step title="최종 확인" icon={<CheckCircleOutlined />} />
      </Steps>

      {currentStep === 0 && (
        <div>
          <div className="mb-6">
            <h4 className="text-sm font-medium mb-3">등록 타입 선택</h4>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="registrationType"
                  value="unregistered"
                  checked={registrationType === 'unregistered'}
                  onChange={(e) => setRegistrationType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">
                  <strong>미등록 상품예약</strong>
                  <span className="text-gray-500 ml-2">(새로운 미등록 상품으로 생성)</span>
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="registrationType"
                  value="registered"
                  checked={registrationType === 'registered'}
                  onChange={(e) => setRegistrationType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">
                  <strong>등록된 상품예약</strong>
                  <span className="text-gray-500 ml-2">(상품코드 매칭하여 기존 상품에 추가)</span>
                </span>
              </label>
            </div>
          </div>
          
          <Dragger
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              handleFileUpload(file);
              return false;
            }}
            showUploadList={false}
            disabled={loading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {fileName || '엑셀 파일을 드래그하거나 클릭하여 업로드'}
            </p>
            <p className="ant-upload-hint">
              xlsx, xls, csv 파일을 지원합니다
            </p>
          </Dragger>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <div>
              <Tag color="blue">{editingData.length}개 주문 그룹</Tag>
              <Tag color="green">{selectedOrders.size}개 선택됨</Tag>
              {registrationType === 'registered' && matchedProducts.length > 0 && (
                <>
                  <Tag color="success">{matchedProducts.filter(m => m.matched).length}개 매칭됨</Tag>
                  {matchedProducts.filter(m => !m.matched).length > 0 && (
                    <Tag color="error">{matchedProducts.filter(m => !m.matched).length}개 매칭 실패</Tag>
                  )}
                </>
              )}
            </div>
            <Space>
              <Button size="small" onClick={selectAllOrders}>전체 선택</Button>
              <Button size="small" onClick={deselectAllOrders}>전체 해제</Button>
            </Space>
          </div>
          
          {registrationType === 'registered' && matchedProducts.some(m => !m.matched) && (
            <Alert
              message="일부 상품 매칭 실패"
              description="일부 예약의 상품코드가 등록된 상품과 매칭되지 않았습니다. 해당 예약은 선택 해제하거나 상품코드를 확인해주세요."
              type="warning"
              showIcon
              className="mb-4"
            />
          )}
          
          <div className="max-h-96 overflow-y-auto space-y-4">
            {editingData.map((order, index) => {
              const matchInfo = matchedProducts[index];
              const isRegisteredMode = registrationType === 'registered';
              const exCheck = existingChecks[index];
              
              return (
                <Card 
                  key={index}
                  size="small"
                  className={`${selectedOrders.has(index) ? 'border-blue-500' : ''} ${isRegisteredMode && !matchInfo?.matched ? 'border-red-300 bg-red-50' : ''}`}
                  title={
                    <div className="flex items-center justify-between">
                      <Checkbox 
                        checked={selectedOrders.has(index)}
                        onChange={() => toggleOrderSelection(index)}
                      >
                        주문 {index + 1}
                      </Checkbox>
                      <div className="flex items-center gap-2">
                        <Tag color="blue">{order.travelerCount}명</Tag>
                        {isRegisteredMode && matchInfo && (
                          matchInfo.matched ? (
                            <Tag color="success" icon={<CheckOutlined />}>매칭 성공</Tag>
                          ) : (
                            <Tag color="error" icon={<CloseOutlined />}>매칭 실패</Tag>
                          )
                        )}
                        {exCheck && (
                          exCheck.exists ? (
                            <Tag color="purple">기존예약 수정</Tag>
                          ) : (
                            <Tag color="geekblue">신규 생성</Tag>
                          )
                        )}
                      </div>
                    </div>
                  }
                >
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>예약일:</strong> {new Date(order.reservationDate).toLocaleDateString('ko-KR')}
                    </div>
                    <div>
                      <strong>행사시작일:</strong> {order.eventStartDate ? new Date(order.eventStartDate).toLocaleDateString('ko-KR') : '-'}
                    </div>
                    <div>
                      <strong>총 금액:</strong> {order.totalAmount.toLocaleString()}원
                    </div>
                    
                    {/* 등록된 상품 모드: 매칭된 상품 정보 표시 */}
                    {isRegisteredMode && matchInfo ? (
                      matchInfo.matched ? (
                        <div className="col-span-2 p-3 bg-green-50 border border-green-200 rounded">
                          <div className="flex items-start gap-2 mb-2">
                            <CheckOutlined className="text-green-600 mt-1" />
                            <div className="flex-1">
                              <div className="font-medium text-green-800 mb-1">매칭된 등록 상품</div>
                              <div className="space-y-1 text-xs">
                                <div><strong>상품명:</strong> {matchInfo.product.상품명}</div>
                                <div className="flex gap-4">
                                  <span><strong>상품코드:</strong> {matchInfo.product.상품코드}</span>
                                  <span><strong>도시:</strong> {matchInfo.product.도시 || '-'}</span>
                                  <span><strong>상품종류:</strong> {matchInfo.product.상품종류 || '-'}</span>
                                </div>
                                <div>
                                  <strong>판매여부:</strong> 
                                  <Tag size="small" color={matchInfo.product.판매여부 ? 'green' : 'red'} className="ml-1">
                                    {matchInfo.product.판매여부 ? '판매중' : '판매중지'}
                                  </Tag>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="col-span-2 p-3 bg-red-50 border border-red-200 rounded">
                          <div className="flex items-start gap-2">
                            <CloseOutlined className="text-red-600 mt-1" />
                            <div className="flex-1">
                              <div className="font-medium text-red-800 mb-1">매칭 실패</div>
                              <div className="text-xs text-red-700">
                                {matchInfo.error}
                                {matchInfo.excelProductCode && (
                                  <div className="mt-1">
                                    <strong>엑셀 상품코드:</strong> {matchInfo.excelProductCode}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      /* 미등록 상품 모드: 기존처럼 상품명 표시 */
                      <div className="col-span-2">
                        <strong>상품명:</strong> {order.productName}
                      </div>
                    )}
                  </div>
                
                <Divider className="my-3" />
                
                <div>
                  <strong className="text-xs text-gray-600">여행객 목록:</strong>
                  <div className="mt-2 space-y-1">
                    {order.travelers.map((traveler, tIndex) => (
                      <div key={tIndex} className="text-xs bg-gray-50 p-2 rounded">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{traveler.name}</span>
                          <span className="text-gray-600">{traveler.phone}</span>
                          <span className="text-purple-600">{traveler.email}</span>
                          <span className="text-blue-600">{traveler.amount.toLocaleString()}원</span>
                          {traveler.discount < 0 && (
                            <span className="text-red-600">{traveler.discount.toLocaleString()}원</span>
                          )}
                        </div>
                        {traveler.memo && (
                          <div className="mt-1 text-gray-500">
                            <strong>비고:</strong> {traveler.memo}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
            })}
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div>
          {/* 진행률 표시: 바만 출력 */}
          <div className="mb-4">
            <Progress 
              percent={progressInfo.total > 0 ? Math.floor((progressInfo.completed / progressInfo.total) * 100) : 0}
              status={loading ? 'active' : (progressInfo.errors > 0 ? 'exception' : 'normal')}
              showInfo={false}
            />
          </div>

          <div className="mb-4 p-4 bg-blue-50 rounded">
            <h4 className="text-lg font-medium mb-2">최종 등록 정보</h4>
            <div className="grid grid-cols-3 gap-4 text-sm mb-3">
              <div>
                <strong>등록할 주문:</strong> {selectedOrders.size}개
              </div>
              <div>
                <strong>총 여행객:</strong> {editingData.filter((_, i) => selectedOrders.has(i)).reduce((sum, order) => sum + order.travelerCount, 0)}명
              </div>
              <div>
                <strong>총 금액:</strong> {editingData.filter((_, i) => selectedOrders.has(i)).reduce((sum, order) => sum + order.totalAmount, 0).toLocaleString()}원
              </div>
            </div>
            <div className="pt-3 border-t border-blue-200">
              <strong>등록 타입:</strong> 
              <Tag color={registrationType === 'registered' ? 'green' : 'blue'} className="ml-2">
                {registrationType === 'registered' ? '등록된 상품예약' : '미등록 상품예약'}
              </Tag>
            </div>
          </div>
          
          <div className="text-sm text-gray-600">
            <ExclamationCircleOutlined className="mr-2" />
            {registrationType === 'registered' 
              ? '선택한 주문들이 상품코드를 매칭하여 등록된 상품에 예약으로 추가됩니다. 계속하시겠습니까?'
              : '선택한 주문들이 미등록상품예약으로 생성됩니다. 계속하시겠습니까?'
            }
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MaritReservationImportModal;
